import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useMemo } from 'react'

import type {
  ColumnDef,
  ExpandedState,
  Header,
  Row,
  Table,
} from '@tanstack/react-table'
import { flexRender } from '@tanstack/react-table'

import ChevronBottomIcon from '@/assets/icons/icon-chevron-bottom.svg'
import { EmptySlot } from '@/components/ui/empty/empty-provider.tsx'
import { ScrollBottomDropTarget } from '@/components/ui/table/expandable-dnd-table/ScrollTargets/ScrollBottomDropTarget.tsx'
import { ScrollTopDropTarget } from '@/components/ui/table/expandable-dnd-table/ScrollTargets/ScrollTopDropTarget.tsx'
import type { Entry } from '@/components/ui/table/expandable-dnd-table/useTreeTable'
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table/table.tsx'
import { classNames } from '@/utils/helpers'

import { DraggableRow } from './DraggableRow'

const rowName = 'row'

export interface TreeTableProps<Node, Value> {
  columns: ColumnDef<Entry<Node>, Value>[]
  onChange: Dispatch<SetStateAction<Entry<Node>[]>>
  table: Table<Entry<Node>>
  expanded?: ExpandedState
  onExpandedChange?: Dispatch<SetStateAction<ExpandedState>>
  preventFolderNesting?: boolean
  groupUncategorized?: boolean
  onMove?: (root: Entry<Node>[], moved: TreeRow<Node>) => void
  disableDrag?: boolean
  enableSorting?: boolean
}

type TreeRow<T> = Row<Entry<T>>

export const TreeTable = <Node, Value = unknown>(
  props: TreeTableProps<Node, Value>,
) => {
  const { columns, table, groupUncategorized, enableSorting, disableDrag } =
    props

  const handleMove = useMove(props)

  const handleHeaderClick = useCallback(
    <TData, TValue>(header: Header<TData, TValue>) =>
      () => {
        if (enableSorting) {
          header.column.toggleSorting()
        }
      },
    [enableSorting],
  )

  const headers = useMemo(() => {
    return table.getHeaderGroups().map((headerGroup) => (
      <TableRow key={headerGroup.id}>
        {headerGroup.headers.map((header) => {
          return (
            <TableHead
              key={header.id}
              onClick={handleHeaderClick(header)}
            >
              <div
                className={classNames({
                  'w-full': header.id.endsWith('-full'),
                  'inline-flex cursor-pointer select-none flex-row items-center gap-1':
                    header.column.getCanSort() && enableSorting,
                  'text-right': header.id === 'actions',
                })}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
                {header.column.getCanSort() && enableSorting && (
                  <div className="ml-2 flex flex-col items-center">
                    <ChevronBottomIcon
                      className={classNames(
                        'h-[8px] w-[16px] rotate-180 stroke-secondary-500 dark:stroke-secondary-100',
                        header.column.getIsSorted() === 'asc' &&
                          'stroke-primary-500',
                      )}
                    />
                    <ChevronBottomIcon
                      className={classNames(
                        'h-[8px] w-[16px] stroke-secondary-500 dark:stroke-secondary-100',
                        header.column.getIsSorted() === 'desc' &&
                          'stroke-primary-500',
                      )}
                    />
                  </div>
                )}
              </div>
            </TableHead>
          )
        })}
      </TableRow>
    ))
  }, [enableSorting, handleHeaderClick, table])

  const rowModelRows = table.getRowModel().rows

  const rows = (() => {
    if (rowModelRows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length}>
            <EmptySlot>{() => 'No results.'}</EmptySlot>
          </TableCell>
        </TableRow>
      )
    }
    const coreRows = table.getCoreRowModel()
    const lastFolderId = coreRows.rows[coreRows.rows.length - 1].id

    return rowModelRows.map((row) => (
      <DraggableRow
        key={row.id}
        onMove={handleMove}
        row={row}
        rowName={rowName}
        disableDrag={
          disableDrag || (groupUncategorized && row.id === lastFolderId)
        }
      />
    ))
  })()

  return (
    <>
      <ScrollTopDropTarget accept={rowName} />
      <div className="grid rounded-2xl bg-card p-5">
        <TableComponent>
          <TableHeader>{headers}</TableHeader>
          <TableBody>{rows}</TableBody>
        </TableComponent>
      </div>
      <ScrollBottomDropTarget accept={rowName} />
    </>
  )
}

function moveFolderAfterFolder<Node>(
  tree: Entry<Node>[],
  origin: TreeRow<Node>,
  target: TreeRow<Node>,
) {
  const updated = [...tree]

  // Remove the origin from its current position
  const [originEntry] = updated.splice(origin.index, 1)

  // Calculate the new index for insertion. The index needs to be adjusted if the origin is removed
  // from a position before the target, as the target's index shifts one place to the left.
  const newIndex = origin.index < target.index ? target.index : target.index + 1

  // Insert the origin back into the updated array at the new index
  updated.splice(newIndex, 0, originEntry)

  // Adjust the order of all folders based on their new indices
  return updated.map((entry, index) => entry._withOrder(index))
}

function moveFileAfterFile<Node>(
  tree: Entry<Node>[],
  origin: TreeRow<Node>,
  target: TreeRow<Node>,
): Entry<Node>[] {
  const entry = origin.original

  if (entry._type !== 'file' || target.original._type !== 'file') {
    return tree
  }

  const targetFolder = target.original._parent()
  const originFolder = entry._parent()

  let hasUpdatedBoth = false

  return tree.map((folder) => {
    if (folder._type !== 'folder') {
      return folder
    }

    const isOriginFolder = folder._id() === originFolder?._id()
    const isTargetFolder = folder._id() === targetFolder?._id()

    if (isOriginFolder || (isTargetFolder && !hasUpdatedBoth)) {
      hasUpdatedBoth = isOriginFolder && isTargetFolder

      return folder._withChildren((nodes) => {
        const nodesCopy = [...nodes]

        const indexOrigin = nodesCopy.findIndex(
          (it) => it._id() === origin.original._id(),
        )
        const indexTarget = nodesCopy.findIndex(
          (it) => it._id() === target.original._id(),
        )

        // First remove file at origin position
        if (isOriginFolder && indexOrigin > -1) {
          nodesCopy.splice(indexOrigin, 1)
        }

        // Then insert the file at the correct index.
        if (isTargetFolder) {
          nodesCopy.splice(indexTarget, 0, entry)
        }

        return nodesCopy.map((it, index) => it._withOrder(index))
      })
    }

    return folder
  })
}

function moveFileIntoFolder<Node>(
  tree: Entry<Node>[],
  origin: TreeRow<Node>,
  target: TreeRow<Node>,
): Entry<Node>[] {
  const entry = origin.original

  if (entry._type !== 'file' || target.original._type !== 'folder') {
    return tree
  }
  const currentParent = entry._parent()

  if (currentParent && target.original._id() == currentParent._id()) {
    // When moving file into its own folder we don't need to do anything.
    return tree
  }

  // Go through the folder nodes and remove it from where the entry
  // currently is in, then add it to where it should go into
  return tree.map((folder) => {
    if (folder._type !== 'folder') {
      return folder
    }
    if (currentParent && folder._id() === currentParent._id()) {
      // Remove the child from where it is currently in
      return folder._withChildren((nodes) =>
        nodes.filter((it) => it._id() !== entry._id()),
      )
    }
    if (folder._id() === target.original._id()) {
      return folder._withChildren((current) => [...current, entry])
    }

    return folder
  })
}

type MoveFunction<Node> = (state: Entry<Node>[]) => Entry<Node>[]

/**
 * Moves a node to a new location within the tree.
 */
function useMove<Node>(
  props: Pick<
    TreeTableProps<Node, never>,
    | 'onChange'
    | 'table'
    | 'preventFolderNesting'
    | 'groupUncategorized'
    | 'onMove'
    | 'onExpandedChange'
  >,
) {
  const { onChange, onMove } = props

  return useCallback(
    (origin: TreeRow<Node>, target: TreeRow<Node>) => {
      const update = (move: MoveFunction<Node>) => {
        onChange((state) => {
          const updated = move(state)

          onMove?.(updated, origin)

          return updated
        })
      }

      if (
        origin.original._type === 'folder' &&
        target.original._type === 'folder'
      ) {
        update((state) => moveFolderAfterFolder(state, origin, target))

        return
      }
      if (
        origin.original._type === 'file' &&
        target.original._type === 'folder'
      ) {
        update((state) => moveFileIntoFolder(state, origin, target))
      }
      if (
        origin.original._type === 'file' &&
        target.original._type === 'file'
      ) {
        update((state) => moveFileAfterFile(state, origin, target))
      }
    },
    [onChange, onMove],
  )
}
